import {
  BufferGeometry,
  CatmullRomCurve3,
  Float32BufferAttribute,
  Vector3,
} from 'three';

const UP = new Vector3(0, 1, 0);

/**
 * Centreline of Bashir Elnifidi Street (Street 60) in Khartoum.
 *
 * Local coordinates are metres: +X is east, -Z is north. The points were
 * derived from the OSM/OSRM centreline of the road between the Wad Madani
 * highway junction (south) and Al-Mashtal Street (north), then simplified to
 * roughly one point every 50–150 m. Short straight extensions tuck the road
 * under the junctions at either end.
 */
const CONTROL_POINTS = [
  [440.2, 2695.2],
  [376.9, 2302.9],
  [355.3, 2179.1],
  [350.5, 2129.4],
  [334.7, 2043.0],
  [315.5, 1920.0],
  [305.0, 1866.4],
  [290.3, 1762.0],
  [278.8, 1699.8],
  [262.2, 1593.3],
  [240.0, 1486.9],
  [230.8, 1425.5],
  [224.2, 1363.9],
  [216.0, 1319.0],
  [180.0, 1092.4],
  [170.8, 1046.7],
  [164.2, 995.3],
  [116.0, 701.5],
  [87.9, 541.3],
  [68.7, 415.2],
  [35.6, 220.4],
  [18.5, 104.5],
  [-16.8, -99.7],
  [-49.6, -308.3],
  [-80.8, -483.5],
  [-102.8, -624.3],
  [-108.1, -667.7],
  [-226.5, -1399.2],
  [-418.7, -2561.5],
];

export const ROAD = {
  /** Full asphalt width, including both carriageways. */
  width: 21.6,
  medianWidth: 4.2,
  laneWidth: 3.55,
  sidewalkWidth: 3.2,
  sidewalkOffset: 12.6,
  curbOffset: 10.85,
};

/** Fuel stations mapped from OSM, on the east side of the northbound lane. */
export const STATIONS = [
  {
    id: 'bashair',
    nameArabic: 'بشائر',
    nameEnglish: 'BASHAIR',
    s: 1383,
    side: 1,
    brand: '#1d6b48',
    accent: '#f0c04e',
  },
  {
    id: 'nabta',
    nameArabic: 'نبتة الستين',
    nameEnglish: 'NABTA 60',
    s: 2322,
    side: 1,
    brand: '#175b78',
    accent: '#e9a33b',
  },
];

/** The two forecourt driveways shared by both station layouts. */
export const STATION_ACCESS = {
  drivewayOffsets: [-16, 16],
  drivewayWidth: 15,
};

/**
 * Cross streets (east–west dirt lanes). Values are distance along the route
 * in metres. Kept clear of both station frontages.
 */
export const CROSS_STREETS = [
  330, 550, 830, 1070, 1580, 1840, 2070, 2560, 2830, 3100, 3370, 3640, 3910,
  4200, 4480, 4760, 5040, 5260,
];

/** Openings in the central median, wide enough for the Hilux to U-turn. */
export const MEDIAN_OPENINGS = [
  { s: 330, width: 26 },
  { s: 830, width: 26 },
  { s: 1190, width: 28 },
  { s: 1580, width: 26 },
  { s: 1840, width: 26 },
  { s: 2070, width: 26 },
  { s: 2560, width: 28 },
  { s: 3100, width: 26 },
  { s: 3640, width: 26 },
  { s: 4200, width: 28 },
  { s: 4760, width: 26 },
  { s: 5260, width: 30 },
];

export function createRoute() {
  const curve = new CatmullRomCurve3(
    CONTROL_POINTS.map(([x, z]) => new Vector3(x, 0, z)),
    false,
    'centripetal',
    0.5,
  );

  const length = curve.getLength();
  const step = 2;
  const count = Math.ceil(length / step);
  const samples = [];

  for (let i = 0; i <= count; i += 1) {
    const t = i / count;
    const position = curve.getPointAt(t);
    const tangent = curve.getTangentAt(t).normalize();
    const right = new Vector3().crossVectors(tangent, UP).normalize();
    const yaw = Math.atan2(-tangent.x, -tangent.z);
    samples.push({ s: t * length, position, tangent, right, yaw });
  }

  return new Route(curve, length, samples, step);
}

export class Route {
  constructor(curve, length, samples, step) {
    this.curve = curve;
    this.length = length;
    this.samples = samples;
    this.step = step;
  }

  frameAt(s) {
    const clamped = Math.max(0, Math.min(this.length, s));
    const t = clamped / this.length;
    const position = this.curve.getPointAt(t);
    const tangent = this.curve.getTangentAt(t).normalize();
    const right = new Vector3().crossVectors(tangent, UP).normalize();
    return {
      s: clamped,
      position,
      tangent,
      right,
      yaw: Math.atan2(-tangent.x, -tangent.z),
    };
  }

  /** A frame shifted laterally: positive = right/east of the northbound direction. */
  offsetFrame(s, lateral = 0) {
    const frame = this.frameAt(s);
    frame.position.addScaledVector(frame.right, lateral);
    return frame;
  }

  /**
   * Build a ribbon that follows the centreline.
   *
   * @param {object} options
   * @param {number} options.start
   * @param {number} options.end
   * @param {number} options.width
   * @param {number} options.offset  lateral centre offset (positive = east)
   * @param {number} options.y
   * @param {number} [options.uvScale]
   * @returns {import('three').BufferGeometry}
   */
  ribbonGeometry({
    start = 0,
    end = this.length,
    width = 1,
    offset = 0,
    y = 0.02,
    uvScale = 8,
  }) {
    const positions = [];
    const uvs = [];
    const indices = [];
    const half = width / 2;

    const clampedStart = Math.max(0, Math.min(this.length, start));
    const clampedEnd = Math.max(0, Math.min(this.length, end));
    const samples = [
      this.frameAt(clampedStart),
      ...this.samples.filter((sample) => sample.s > clampedStart && sample.s < clampedEnd),
      this.frameAt(clampedEnd),
    ];

    for (const sample of samples) {
      for (const side of [-1, 1]) {
        const point = sample.position
          .clone()
          .addScaledVector(sample.right, offset + side * half);
        positions.push(point.x, y, point.z);
        uvs.push(side < 0 ? 0 : width / uvScale, sample.s / uvScale);
      }
    }

    const rows = positions.length / 6;
    for (let row = 0; row < rows - 1; row += 1) {
      const a = row * 2;
      const b = a + 1;
      const c = a + 2;
      const d = a + 3;
      // Winding chosen so the face points up (+Y).
      indices.push(a, b, c, b, d, c);
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
  }
}

export { CONTROL_POINTS };
